import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const SITE_TITLE_SUFFIX = " — Race Manager";

export interface PageMetaInput {
  /** Literal page title; wins over titleKey. Null/undefined = not set. */
  title?: string | null;
  /** i18n key for the title, e.g. "pages:titles.events". */
  titleKey?: string;
  /** Literal meta description; wins over descriptionKey. */
  description?: string | null;
  /** i18n key for the description. */
  descriptionKey?: string;
}

/**
 * Sets the browser tab title and the meta description for the current page.
 * Titles get the " — Race Manager" suffix; a falsy title leaves the shell
 * default (index.html) in place. The description replaces the existing
 * <meta name="description"> shipped in index.html — never adds a duplicate.
 *
 * `resetKey` (e.g. the current pathname) re-applies the same resolved values
 * when it changes — used by RouteMeta so a param-only navigation (same route,
 * different id) restores the static title after a page-level dynamic title.
 */
export function usePageMeta(
  { title, titleKey, description, descriptionKey }: PageMetaInput,
  resetKey?: string | number,
): void {
  const { t } = useTranslation();
  const resolvedTitle = title ?? (titleKey ? t(titleKey) : null);
  const resolvedDescription =
    description ?? (descriptionKey ? t(descriptionKey) : null);

  useEffect(() => {
    if (resolvedTitle) {
      document.title = `${resolvedTitle}${SITE_TITLE_SUFFIX}`;
    }
  }, [resolvedTitle, resetKey]);

  useEffect(() => {
    if (!resolvedDescription) {
      return;
    }
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", resolvedDescription);
  }, [resolvedDescription, resetKey]);
}
